/**
 * Signed-in list: what needs my signature, what is still moving, what is done.
 */
import {
  base64ToBlob,
  downloadBlob,
  getEnvelopeFile,
  listMyEnvelopes,
  messageOf,
  ApiError,
} from '../api';
import type { EnvelopeSummary, Signer } from '../api';
import { announce, el, formatDate, joinNames, messageSlot, onClickAsync, setDocumentTitle } from '../ui';
import { envelopeStatusPill, isMe, outstandingSigners, signerRoster } from './roster';
import type { Teardown, ViewContext } from './view';

interface Grouped {
  awaiting: EnvelopeSummary[];
  inProgress: EnvelopeSummary[];
  completed: EnvelopeSummary[];
}

function mySignerOn(envelope: EnvelopeSummary, email: string): Signer | undefined {
  return envelope.signers.find((signer) => isMe(signer, email));
}

function group(envelopes: EnvelopeSummary[], email: string): Grouped {
  const grouped: Grouped = { awaiting: [], inProgress: [], completed: [] };
  for (const envelope of envelopes) {
    const mine = mySignerOn(envelope, email);
    if (envelope.status === 'completed') grouped.completed.push(envelope);
    else if (envelope.status === 'draft' || envelope.status === 'voided')
      grouped.inProgress.push(envelope);
    else if (mine && mine.status !== 'signed') grouped.awaiting.push(envelope);
    else grouped.inProgress.push(envelope);
  }
  return grouped;
}

export function mountInbox(host: HTMLElement, ctx: ViewContext): Teardown {
  setDocumentTitle('Your documents');
  const email = ctx.user?.email ?? '';
  let disposed = false;

  host.replaceChildren(el('p', { class: 'muted', text: 'Loading your documents...' }));

  function card(envelope: EnvelopeSummary): HTMLElement {
    const mine = mySignerOn(envelope, email);
    const needsMe = envelope.status !== 'completed' && envelope.status !== 'voided' && mine && mine.status !== 'signed';
    const waitingOn = outstandingSigners(envelope.signers).map((signer) => signer.name);
    const messages = messageSlot();

    const open = el('button', {
      class: needsMe ? 'button button-primary' : 'button button-quiet',
      text: needsMe ? 'Review and sign' : 'View document',
      attrs: { type: 'button' },
      on: { click: () => ctx.navigate(`/e/${envelope.$id}`) },
    });

    const actions = el('div', { class: 'card-actions', children: [open] });

    if (envelope.status === 'completed') {
      const download = el('button', {
        class: 'button button-quiet',
        text: 'Download signed PDF',
        attrs: { type: 'button' },
      });
      onClickAsync(download, 'Preparing...', async () => {
        messages.clear();
        try {
          const file = await getEnvelopeFile(envelope.$id, true);
          downloadBlob(base64ToBlob(file.base64, file.contentType), file.filename);
          announce(`Downloading ${file.filename}.`);
        } catch (err) {
          messages.show('error', messageOf(err, 'We could not prepare that download.'));
        }
      });
      actions.append(download);
    }

    const completedOn = formatDate(envelope.completedAt);
    const subtitle =
      envelope.status === 'completed'
        ? completedOn
          ? `Fully executed ${completedOn}`
          : 'Fully executed'
        : waitingOn.length > 0
          ? `Waiting on ${joinNames(waitingOn)}`
          : 'No signatures outstanding';

    return el('article', {
      class: `card doc-card${needsMe ? ' doc-card-action' : ''}`,
      children: [
        el('div', {
          class: 'doc-head',
          children: [
            el('div', {
              class: 'doc-heading',
              children: [
                el('h3', { class: 'doc-title', text: envelope.title }),
                el('p', { class: 'doc-subtitle', text: subtitle }),
              ],
            }),
            envelopeStatusPill(envelope.status),
          ],
        }),
        signerRoster(envelope.signers, email),
        messages.node,
        actions,
      ],
    });
  }

  function section(title: string, envelopes: EnvelopeSummary[]): HTMLElement | null {
    if (envelopes.length === 0) return null;
    return el('section', {
      class: 'doc-section',
      children: [
        el('h2', { class: 'section-title', text: title }),
        el('div', { class: 'doc-list', children: envelopes.map(card) }),
      ],
    });
  }

  function renderEmpty(): HTMLElement {
    return el('section', {
      class: 'card card-narrow',
      children: [
        el('h1', { class: 'title', text: 'Nothing to sign yet' }),
        el('p', {
          class: 'lede',
          text: 'When someone sends you a document, it will appear here and you will get an email.',
        }),
        el('p', { class: 'fine-print', text: `Signed in as ${email}.` }),
      ],
    });
  }

  void (async () => {
    try {
      const { envelopes } = await listMyEnvelopes();
      if (disposed) return;

      if (envelopes.length === 0) {
        host.replaceChildren(renderEmpty());
        announce('You have no documents yet.');
        return;
      }

      const grouped = group(envelopes, email);
      const sections = [
        section('Awaiting your signature', grouped.awaiting),
        section('In progress', grouped.inProgress),
        section('Completed', grouped.completed),
      ].filter((node): node is HTMLElement => node !== null);

      host.replaceChildren(
        el('h1', { class: 'title', text: 'Your documents' }),
        ...sections,
      );

      announce(
        grouped.awaiting.length > 0
          ? `${grouped.awaiting.length} document${grouped.awaiting.length === 1 ? '' : 's'} awaiting your signature.`
          : 'No documents are awaiting your signature.',
      );
    } catch (err) {
      if (disposed) return;
      if (err instanceof ApiError && err.isAuthError) {
        ctx.refresh();
        return;
      }
      const messages = messageSlot();
      const retry = el('button', {
        class: 'button button-primary',
        text: 'Try again',
        attrs: { type: 'button' },
        on: { click: () => ctx.refresh() },
      });
      host.replaceChildren(
        el('section', {
          class: 'card card-narrow',
          children: [
            el('h1', { class: 'title', text: 'We could not load your documents' }),
            messages.node,
            retry,
          ],
        }),
      );
      messages.show('error', messageOf(err, 'Something went wrong. Please try again.'));
    }
  })();

  return () => {
    disposed = true;
  };
}
